import { getColorVariable } from "@components/common/utils/helper";
import { type InputCheckboxProps } from "./input-checkbox";
import { KeyValueObject } from "@components/common/types/general";

export function getVerticalCenter(
    size: InputCheckboxProps["size"],
    verticalCenter: InputCheckboxProps["verticalCenter"],
    theme: any,
): KeyValueObject | null {
    if (size === "sm") {
        if (verticalCenter) {
            return {
                paddingTop: "7px",
                paddingBottom: "7px",
            };
        }
    }
    if (size === "lg") {
        if (verticalCenter) {
            return {
                paddingTop: theme.spacing[3.5],
                paddingBottom: theme.spacing[3.5],
            };
        }
    }
    if (verticalCenter) {
        return {
            paddingTop: theme.spacing[1.5],
            paddingBottom: theme.spacing[1.5],
        };
    }
    return null;
}

export function getBackgroundColor(
    color: InputCheckboxProps["color"],
    checkSwitch: InputCheckboxProps["checkSwitch"],
    checked: boolean,
    theme: any,
): KeyValueObject {
    if (checked) {
        return {
            backgroundColor: getColorVariable(color, theme, {
                gray: theme.colors.gray[400],
                white: theme.colors.primary[500],
                colorize: theme.colors[color || "primary"][500],
                default: theme.colors.gray[950],
            }),
        };
    }
    if (checkSwitch) {
        return {
            backgroundColor: getColorVariable(color, theme, {
                gray: theme.colors.gray[100],
                white: theme.colors.gray[100],
                colorize: theme.colors.gray[100],
                default: theme.colors.gray[100],
            }),
        };
    }
    return {
        backgroundColor: getColorVariable(color, theme, {
            gray: theme.colors.white,
            white: theme.colors.white,
            colorize: theme.colors.white,
            default: theme.colors.white,
        }),
    };
}

export function getBorderRadius(rounded: InputCheckboxProps["rounded"], theme: any): KeyValueObject | null {
    if (rounded === "md") {
        return { borderRadius: theme.borderRadius.DEFAULT };
    }
    if (rounded === "lg") {
        return { borderRadius: theme.borderRadius.lg };
    }
    if (rounded === "full") {
        return { borderRadius: theme.borderRadius.full };
    }
    return null;
}

export function getColor(color: InputCheckboxProps["color"], theme: any): KeyValueObject {
    return {
        color: getColorVariable(color, theme, {
            gray: theme.colors.white,
            white: theme.colors.white,
            colorize: theme.colors.white,
            default: theme.colors.white,
        }),
    };
}

export function getBoxShadow(
    shadow: InputCheckboxProps["shadow"],
    color: InputCheckboxProps["color"],
    error: InputCheckboxProps["error"],
    checked: InputCheckboxProps["checked"],
    theme: any,
): KeyValueObject | null {
    if (error) {
        return {
            boxShadow: theme.boxShadow["button-red-focus"],
        };
    }
    if (shadow) {
        if (checked) {
            const shadow = getColorVariable(color, theme, {
                gray: "button",
                white: "button-primary",
                colorize: `button-${color}`,
                default: "button",
            });
            return {
                boxShadow: theme.boxShadow[shadow] || theme.boxShadow.DEFAULT,
            };
        }
        const shadow = getColorVariable(color, theme, {
            gray: "default",
            white: "default",
            colorize: "default",
            default: "default",
        });
        return {
            boxShadow: theme.boxShadow[shadow] || theme.boxShadow.DEFAULT,
        };
    }
    return null;
}

export function getBoxShadowFocus(
    color: InputCheckboxProps["color"],
    error: InputCheckboxProps["error"],
    checked: InputCheckboxProps["checked"],
    theme: any,
): KeyValueObject {
    if (error) {
        return {
            boxShadow: theme.boxShadow["button-red-focus"],
        };
    }
    if (checked) {
        const shadow = getColorVariable(color, theme, {
            gray: "button-focus",
            white: "button-primary-focus",
            colorize: `button-${color}-focus`,
            default: "button-focus",
        });
        return {
            boxShadow: theme.boxShadow[shadow] || theme.boxShadow.DEFAULT,
        };
    }
    const shadow = getColorVariable(color, theme, {
        gray: "button-focus",
        white: "button-focus",
        colorize: "button-focus",
        default: "button-focus",
    });
    return {
        boxShadow: theme.boxShadow[shadow] || theme.boxShadow.DEFAULT,
    };
}

export function getSize(size: InputCheckboxProps["size"], checkSwitch: InputCheckboxProps["checkSwitch"], theme: any): KeyValueObject {
    if (size === "sm") {
        if (checkSwitch) {
            return {
                width: theme.spacing[7],
                height: theme.spacing[3.5],
            };
        }
        return {
            width: theme.spacing[3.5],
            height: theme.spacing[3.5],
        };
    }
    if (size === "lg") {
        if (checkSwitch) {
            return {
                width: theme.spacing[12],
                height: theme.spacing[6],
            };
        }
        return {
            width: theme.spacing[6],
            height: theme.spacing[6],
        };
    }
    if (checkSwitch) {
        return {
            width: theme.spacing[10],
            height: theme.spacing[5],
        };
    }
    return {
        width: theme.spacing[5],
        height: theme.spacing[5],
    };
}

export function getIconSize(size: InputCheckboxProps["size"], checkSwitch: InputCheckboxProps["checkSwitch"], theme: any): KeyValueObject {
    if (checkSwitch) {
        if (size === "sm") {
            return {
                width: theme.spacing[3.5],
                height: theme.spacing[3.5],
            };
        }
        if (size === "lg") {
            return {
                width: theme.spacing[6],
                height: theme.spacing[6],
            };
        }
        return {
            width: theme.spacing[5],
            height: theme.spacing[5],
        };
    }
    if (size === "sm") {
        return {
            width: theme.spacing[3],
            height: theme.spacing[3],
        };
    }
    return {
        width: theme.spacing[4],
        height: theme.spacing[4],
    };
}
